import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import YoutubeVideo from 'components/YoutubeVideo';
import { getYoutubeId } from 'helpers/resources';

const FsLightbox = dynamic(() => import('fslightbox-react'));

const IFRAME_WIDTH = 1920;
const IFRAME_HEIGHT = 1080;
const IMAGE_SOURCE = /^[^?#]+\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#]|$)/i;

/** @type {import("./types").default} */
const Lightbox = ({ toggler, sources, captions, ...props }) => {
  const [lightboxState, setLightboxState] = useState({ loaded: toggler });
  useEffect(() => {
    if (toggler) {
      setLightboxState({ loaded: true });
    }
  }, [lightboxState.loaded, toggler]);

  if (!lightboxState.loaded) {
    return null;
  }

  const getSource = (source) => {
    if (IMAGE_SOURCE.test(source)) {
      return source;
    }

    if (source.includes('wistia') || source.includes('vimeo')) {
      return (
        <iframe
          data-testid="lightbox-iframe"
          title={source}
          src={source}
          className="Lightbox-video"
          width={`${IFRAME_WIDTH}px`}
          height={`${IFRAME_HEIGHT}px`}
          allowFullScreen="allowfullscreen"
          scrolling="no"
          allow="autoplay; fullscreen"
        />
      );
    } else if (source.includes('youtube') || source.includes('youtu.be')) {
      const youtubeId = getYoutubeId(source);
      if (youtubeId) {
        return (
          <div
            className="Lightbox-video"
            style={{ width: IFRAME_WIDTH, height: IFRAME_HEIGHT }}
          >
            <YoutubeVideo video={{ videoId: youtubeId }} loading="lazy" />
          </div>
        );
      }
    }

    return source;
  };

  const getTypes = (source) => {
    // Image type probing uses XHR, but image CDNs do not always allow CORS.
    if (IMAGE_SOURCE.test(source)) {
      return 'image';
    }

    return null;
  };

  return (
    <FsLightbox
      className="Lightbox"
      captions={captions}
      disableThumbs={true}
      sources={sources.map(getSource)}
      types={sources.map(getTypes)}
      {...props}
      openOnMount={true}
      toggler={toggler}
    />
  );
};

export default Lightbox;
